/* eslint-disable max-len*/
import React, {Suspense, useEffect, useState} from 'react';
import ReactDOM from 'react-dom';
import {Helmet} from 'react-helmet';
import {useHistory, useLocation, useParams} from 'react-router-dom';
import clsx from 'clsx';

import {StepPhone} from '../../../modules/comments/components/modal-comments/authenticated-overlay/components/step-phone';
import {USER_ROLES} from '../../../website-ui/constants/user';
import {useUser} from '../../../website-ui/hooks/use-user';
import {AuthAlert} from '../../components/AuthAlert';
import {ShareMenu} from '../../components/ShareMenu';
import Voting from '../../components/Voting';
import {CURRENT_BASE, CURRENT_ORIGIN, RATING_API} from '../../constants';
import {disableScroll, enableScroll} from '../../helpers';
import {candidateSeoTexts} from '../../seoTemplates';

import Loader from './../../components/Loader';

import styles from './index.module.css';

const ReactMarkdown = React.lazy(() => import(/* webpackChunkName: "markdownLib" */'react-markdown'));

export default ({seoTexts, setSeoTexts, categories}) => {
  const {category, nomination, candidate} = useParams();
  const {isOpen, content} = useLocation();
  const currentNominaitonId = categories.find(c => c.slug === category).nominations.find(n => n.slug === nomination).id;
  const history = useHistory();
  const [modalData, setModalData] = useState({isOpen, content});
  const [isOpenTelForm, setIsOpenTelForm] = useState(false);
  const {isAuthenticated, role} = useUser();
  const isLogged = isAuthenticated && role === USER_ROLES.EXTENDED;
  const [alertClassName, setAlertClassName] = useState(clsx({
    [styles.defaultAuthWrapper]: true,
    [styles.authWrapper]: false
  }));

  const handleAddPhoneNumber = () => {
    setIsOpenTelForm(true);
  };

  useEffect(() => {
    if (category === 'auth') {
      return null;
    }
    if (!content) {
      return fetch(`${RATING_API}nominations/${currentNominaitonId}`)
        .then(res => {
          if (res.status !== 200) {
            res.json()
              .then(data => {
                throw new Error(data.message);
              })
              .catch(console.error);
            throw new Error(`Something wrong on fetching nominations with id=${currentNominaitonId}!`);
          }
          return res;
        })
        .then(resp => resp.json())
        .then(data => {
          if (data?.candidates.length > 0) {
            const currentCandidate = data.candidates.find(c => c.candidate.slug === candidate);

            setSeoTexts(candidateSeoTexts(category, nomination, currentCandidate.candidate, categories));
            setModalData({isOpen: true, content: currentCandidate});
          } else {
            history.replace('/not-found/');
          }
        })
        .catch(console.error);
    }
    setSeoTexts(candidateSeoTexts(category, nomination, content.candidate, categories));
    setModalData({isOpen, content});
  }, [candidate]);

  useEffect(() => {
    disableScroll();
    return () => enableScroll();
  }, []);

  if (category === 'auth') {
    return null;
  }

  if (!modalData.isOpen) {
    return null;
  }

  const closeModal = {
    pathname: `/${category}/${nomination}/`
  };

  const handleVoteUnlogged = () => {
    if (!isLogged) {
      setAlertClassName(clsx({
        [styles.defaultAuthWrapper]: true,
        [styles.authWrapper]: true
      }));
      setTimeout(() => {
        setAlertClassName(clsx({
          [styles.defaultAuthWrapper]: true,
          [styles.authWrapper]: false
        }));
      }, 1400);
    }
  };

  const hideFormNumberForm = () => setIsOpenTelForm(false);
  const nullishFunction = () => {};

  return ReactDOM.createPortal(
    <div className={styles.modal}>
      <Helmet>
        <title>{seoTexts.title}</title>
        <meta
          name="description"
          content={seoTexts.description}
        />
        <meta property="og:title" content={seoTexts.title} />
        <meta property="og:image" content={modalData.content.candidate.photo?.url} />
        <meta name="twitter:title" content={seoTexts.title} />
        <meta property="vk:title" content={seoTexts.title} />
        <meta name="twitter:description" content={seoTexts.description} />
        <meta property="og:description" content={seoTexts.description} />
        <meta property="vk:description" content={seoTexts.description} />
        <meta property="og:url" content={`${CURRENT_ORIGIN}${CURRENT_BASE}${seoTexts.canonical}`} />
        <meta name="twitter:url" content={`${CURRENT_ORIGIN}${CURRENT_BASE}${seoTexts.canonical}`} />
        <meta property="vk:url" content={`${CURRENT_ORIGIN}${CURRENT_BASE}${seoTexts.canonical}`} />
        <link rel="canonical" href={`${CURRENT_ORIGIN}${CURRENT_BASE}${seoTexts.canonical}`} />
      </Helmet>
      <div className={styles.modalContent}>
        <button
          className={styles.backButton}
          onClick={() => history.push(closeModal)}
        >
          <svg width="15" height="15" viewBox="0 0 15 15" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M1.75734 7.35912L1.61941 7.49929L1.75725 7.63957L2.48104 8.37617L2.48103 8.37618L2.48233 8.37748L6.09976 11.992L6.24124 12.1333L6.38261 11.9918L7.08942 11.2844L7.23079 11.143L7.08931 11.0016L4.28546 8.20005H13.0945H13.2945V8.00005V7.00005V6.80005H13.0945H4.28575L7.08931 3.99878L7.23079 3.85741L7.08942 3.71593L6.38261 3.00854L6.24124 2.86706L6.09976 3.00842L2.44888 6.65633L2.44887 6.65632L2.44768 6.65753L1.75734 7.35912Z" fill="#757575" stroke="#757575" strokeWidth="0.4" />
          </svg>
          <span className={styles.backButtonText}>Назад</span>
        </button>
        <div className={styles.topContent}>
          <img src={modalData.content.candidate.photo?.url} alt={modalData.content.candidate.fullname} className={styles.avatar} />
          <h2 className={styles.modalHeading}>{modalData.content.candidate.fullname}</h2>
          <p className={styles.modalDescription}>{modalData.content.candidate.description}</p>
          <Voting
            entityId={modalData.content.candidate?.id}
            sendingVote={false}
            setSendingVote={() => {}}
            entityName={modalData.content.candidate?.fullname}
            currentNominationId={currentNominaitonId}
            candidateVotes={modalData.content?.votes}
            className={styles.votes}
            onVoteUnlogged={handleVoteUnlogged}
          />
          {!isLogged && !isOpenTelForm && <AuthAlert className={alertClassName} onTelupdateClick={handleAddPhoneNumber} inModal />}
          {isOpenTelForm && (
            <div className={styles.phoneNumberWrapper}>
              <StepPhone
                goNext={hideFormNumberForm}
                onPhoneCancelClick={hideFormNumberForm}
                onPhoneChangeSuccess={hideFormNumberForm}
                onPhoneSubmitClick={nullishFunction}
                onSMSCodeSubmitClick={nullishFunction}
              />
            </div>
          )}
        </div>
        <ShareMenu className={clsx({[styles.shareMenu]: isLogged})} />
        <div className={styles.description}>
          <Suspense fallback={<Loader />}>
            <ReactMarkdown>{modalData.content.candidate.content}</ReactMarkdown>
          </Suspense>
        </div>
      </div>
      <div
        onClick={() => history.push(closeModal)}
        className={styles.backDrop}
      />
    </div>
    , document.body);
};
